import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import AdminPageHeader from '../components/common/AdminPageHeader';
import PatientCardHeader from '../components/patientcard/PatientCardHeader';
import PatientCardAbha from '../components/patientcard/PatientCardAbha';
import PatientTimeline from '../components/patientcard/PatientTimeline';
import { getClinicalRecord } from '../services/api/patientCardApi';
import { PATIENT_CARD_ERROR_CODE } from '../constants/patientCard';
import './PatientCardPage.css';

/**
 * PatientCardPage (EPIC PATIENT-CARD, F5) — `/org/patients/:patientId`.
 * Admin-only, read-only (D6) full page: page shell + demographics header +
 * the section scaffolding later tasks (F6-F8) render into. The server is
 * the real ADMIN-only wall (§1.0); route-level UI gating is wired
 * separately (F9), on the shared `App.js` / `routes.js`.
 *
 * ONE aggregator call per mount (§1.2 `GET .../clinical-record`, default
 * sections — already includes `patient`, so no separate §1.1 identity call
 * is needed). `fetchRecord` is memoized on `patientId` alone, so the mount
 * effect's dependency is effectively `[patientId]` — a re-render or an
 * unrelated state change cannot re-trigger it. The Retry action calls the
 * same memoized function directly (an explicit user action), never a
 * second effect dependency. (In dev, StrictMode mounts twice, so the call
 * count is bounded at 2 — production mounts once.)
 */
export default function PatientCardPage() {
  const { patientId } = useParams();
  const { isDark } = useTheme();

  // 'loading' | 'ready' | 'not_found' | 'error'
  const [loadState, setLoadState] = useState('loading');
  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState('');

  // bk-857 — NO mounted-ref guard here, deliberately. The page used to hold a
  // component-level `mountedRef` that only its cleanup ever wrote. React 18
  // StrictMode runs mount → cleanup → mount in dev on the SAME refs, so the
  // cleanup latched the flag to false and every 200 response was then dropped
  // by `if (!mountedRef.current) return` — the skeleton never resolved. The
  // guard bought nothing to begin with: React 18 no longer warns on a setState
  // after unmount (it is a silent no-op), so there is nothing left to suppress.
  const fetchRecord = useCallback(async () => {
    if (!patientId) return;
    setLoadState('loading');
    setLoadError('');
    const res = await getClinicalRecord(patientId);
    if (res.success) {
      setRecord(res.data);
      setLoadState('ready');
    } else if (res.code === PATIENT_CARD_ERROR_CODE.PATIENT_NOT_FOUND) {
      setRecord(null);
      setLoadState('not_found');
    } else {
      setLoadError(res.error || 'Something went wrong loading this patient.');
      setLoadState('error');
    }
  }, [patientId]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const patientSection = record?.sections?.patient || null;

  useEffect(() => {
    const patient = patientSection?.item;
    if (patient) {
      const name = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
      document.title = name ? `${name} · ArogyaKhata` : 'Patient Card · ArogyaKhata';
    }
  }, [patientSection]);

  return (
    <div className={`pcard-page ${isDark ? 'pcard-page-dark' : ''}`}>
      <AdminPageHeader />
      <main className="pcard-main">
        {loadState === 'loading' && (
          <div data-testid="patient-card-skeleton" className="pcard-skeleton" aria-live="polite" aria-busy="true">
            <div className="pcard-skel-line pcard-skel-avatar" />
            <div className="pcard-skel-body">
              <div className="pcard-skel-line" style={{ width: '40%' }} />
              <div className="pcard-skel-line" style={{ width: '65%' }} />
            </div>
          </div>
        )}

        {loadState === 'not_found' && (
          <div data-testid="patient-card-not-found" className="pcard-empty">
            <p className="pcard-empty-title">Patient not found</p>
            <p className="pcard-empty-sub">
              This patient doesn't exist, or belongs to a different organisation.
            </p>
          </div>
        )}

        {loadState === 'error' && (
          <div data-testid="patient-card-load-error" className="pcard-empty">
            <p className="pcard-empty-title">{loadError}</p>
            <button type="button" data-testid="patient-card-page-retry" className="pcard-retry-btn" onClick={fetchRecord}>
              Retry
            </button>
          </div>
        )}

        {loadState === 'ready' && (
          <>
            <PatientCardHeader
              patient={patientSection?.item}
              status={patientSection?.status}
              reasonCode={patientSection?.reason_code}
              onRetry={fetchRecord}
            />

            <PatientCardAbha patient={patientSection?.item} isDark={isDark} />

            {/* bk-826 (F10 adjacent finding) — this slot was scaffolded by F5
                for F6's PatientTimeline but never actually mounted at
                integration; the on-screen card rendered demographics only.
                Wiring it in is required for F10 (print of what's on screen)
                to have a screen at all — additive, no existing behaviour
                changes. */}
            <section data-testid="patient-card-timeline-slot" className="pcard-timeline-slot">
              {/* Q2 — the SAME memoized aggregator refetch the header's
                  Retry already uses, handed to the lane-failure notices'
                  `Try again`. Click-only: the page swaps to its loading
                  state on click, so the control unmounts and one click can
                  never produce more than one request. */}
              <PatientTimeline
                sections={record?.sections}
                generatedAt={record?.generated_at}
                onRetry={fetchRecord}
              />
            </section>
          </>
        )}
      </main>
    </div>
  );
}
